import { create } from 'zustand';
import type { DelegationCard, Room } from '../types';

interface DelegationPokerStore {
  // Cards (7 delegation levels)
  cards: DelegationCard[];
  
  // Room state
  roomInfo: Room | null;
  
  // Current user's vote for each situation
  userVotes: Map<string, string>; // situationId -> cardId
  
  // Actions
  setCards: (cards: DelegationCard[]) => void;
  setRoomInfo: (room: Room) => void;
  setUserVote: (situationId: string, cardId: string) => void;
  clearUserVote: (situationId: string) => void;
  resetStore: () => void;
}

const defaultCards: DelegationCard[] = [
  { id: 'tell', level: 1, name: 'Tell', description: 'I will tell them what to do' },
  { id: 'sell', level: 2, name: 'Sell', description: 'I will try and sell it to them' },
  { id: 'consult', level: 3, name: 'Consult', description: 'I will consult and then decide' },
  { id: 'agree', level: 4, name: 'Agree', description: 'We will agree together' },
  { id: 'advise', level: 5, name: 'Advise', description: 'I will advise but they decide' },
  { id: 'inquire', level: 6, name: 'Inquire', description: 'I will inquire after they decide' },
  { id: 'delegate', level: 7, name: 'Delegate', description: 'I will fully delegate' },
];

export const useDelegationPokerStore = create<DelegationPokerStore>((set) => ({
  cards: defaultCards,
  roomInfo: null,
  userVotes: new Map(),

  setCards: (cards) => set({ cards }),
  
  setRoomInfo: (room) => set({ roomInfo: room }),
  
  setUserVote: (situationId, cardId) =>
    set((state) => {
      const newVotes = new Map(state.userVotes);
      newVotes.set(situationId, cardId);
      return { userVotes: newVotes };
    }),
  
  clearUserVote: (situationId) =>
    set((state) => {
      const newVotes = new Map(state.userVotes);
      newVotes.delete(situationId);
      return { userVotes: newVotes };
    }),
  
  resetStore: () =>
    set({
      cards: defaultCards,
      roomInfo: null,
      userVotes: new Map(),
    }),
}));
